import React from 'react';
import PropTypes from 'prop-types';
import Relay from 'react-relay';
import PagingGrid, {defaultPageSize, processPagingParams} from '../fw/PagingGrid.react.js';
import AppPanel from '../fw/AppPanel.react.js';
import {injectIntl, defineMessages, FormattedMessage, IntlShape} from 'react-intl';
import {Link} from 'react-router';

import types from '../../common/model-api/types.js';
import urlMapping from '../app/URLMapping.js';
import {wrapNotFound} from '../app/NotFoundHandler.react.js';

import RetryIncomingEmailsMutation from '../flow-defs/RetryIncomingEmailsMutation.react';
import {showJobNotification} from '../app/notification';
import ConfirmDialog from '../fw/ConfirmDialog.react';
import LoadingButton from '../fw/LoadingButton.react';
import { User } from '../../common/types/types';

// Preload following screens
import(/* webpackChunkName: "FlowIncomingEmailView" */ './FlowIncomingEmailView.react.js');

const messages = defineMessages({
    title: {id: 'flowIncomingEmailList.title', 'defaultMessage': 'List of Incoming emails'},
    retryFailedIncomingEmailsButton: {id: 'FlowIncomingEmailList.retryFailedIncomingEmailsButton', 'defaultMessage': 'Retry failed recevied e-mails'},
	retryFailedIncomingEmailsDescription: {id: 'FlowIncomingEmailList.retryFailedIncomingEmailsDescription', 'defaultMessage': 'Do you really want to retry failed received e-mails?'},
    retryUnprocessedIncomingEmailsButton: {id: 'FlowIncomingEmailList.retryUnprocessedIncomingEmailsButton', 'defaultMessage': 'Retry unprocessed recevied e-mails'},
	retryUnprocessedIncomingEmailsDescription: {id: 'FlowIncomingEmailList.retryUnprocessedIncomingEmailsDescription', 'defaultMessage': 'Do you really want to retry unprocessed received e-mails?'},
});

interface FlowIncomingEmailListProps {
    intl: IntlShape;
    relay?: object;
    user: User;
}

class FlowIncomingEmailList extends React.Component<FlowIncomingEmailListProps> {

    typeName = 'flowIncomingEmails';

    static propTypes = {
        intl: PropTypes.object.isRequired,
        relay: PropTypes.object,
        user: PropTypes.object.isRequired,
    };

    constructor() {
        super();
    }

    onClickRetryFailedIncomingEmails() {
		return new Promise((resolve, reject) => {
			let confirmDialogConfig  = {
				message: this.props.intl.formatMessage(messages.retryFailedIncomingEmailsDescription),
				confirm: this.props.intl.formatMessage(messages.retryFailedIncomingEmailsButton),
				confirmStyle: 'btn-danger'
			};

			return ConfirmDialog(this.props.intl, confirmDialogConfig).then(() => {
				return this.retryFailedIncomingEmails().then(() => {
					return resolve();
				});
			}).catch(() => {
				resolve();
			});
		});
	}



    retryFailedIncomingEmails() {
        let { user } = this.props;

		return new Promise((resolve, reject) => {
            let onSuccess = (response) => {
                if (response.retryIncomingEmails.job) {
                    showJobNotification(this.props.intl, response.retryIncomingEmails.job);
                }

                resolve();
            };

			this.props.relay.commitUpdate(
				new RetryIncomingEmailsMutation({
                    organisation_id: user.organisation.realId,
                    flow_id: user.organisation.flow.realId,
					failed: true,
				}), {onSuccess: onSuccess, onFailure: reject}
			);
		});
	}

    onClickRetryUnprocessedIncomingEmails() {
		return new Promise((resolve, reject) => {
			let confirmDialogConfig  = {
				message: this.props.intl.formatMessage(messages.retryUnprocessedIncomingEmailsDescription),
				confirm: this.props.intl.formatMessage(messages.retryUnprocessedIncomingEmailsButton),
				confirmStyle: 'btn-danger'
			};

			return ConfirmDialog(this.props.intl, confirmDialogConfig).then(() => {
				return this.retryUnprocessedIncomingEmails().then(() => {
					return resolve();
				});
			}).catch(() => {
				resolve();
			});
		});
	}



    retryUnprocessedIncomingEmails() {
        let { user } = this.props;

		return new Promise((resolve, reject) => {
            let onSuccess = (response) => {
                if (response.retryIncomingEmails.job) {
                    showJobNotification(this.props.intl, response.retryIncomingEmails.job);
                }

                resolve();
            };

			this.props.relay.commitUpdate(
				new RetryIncomingEmailsMutation({
                    organisation_id: user.organisation.realId,
                    flow_id: user.organisation.flow.realId,
					processed: false,
				}), {onSuccess: onSuccess, onFailure: reject}
			);
		});
	}


    render() {
        let flow =  this.props.user.organisation.flow;
        const {flowIncomingEmails} = flow;

        const list = flowIncomingEmails;
        let type = types[this.typeName];

        return (
            <AppPanel breadcrumbParams={[
                {type: types['organisations'], data: this.props.user.organisation},
                {type: types['flows'], data: flow},
            ]} title={this.props.intl.formatMessage(messages.title)}>
                <div>

                    <div aria-label="..." className="btn-group" role="group" style={{paddingRight: '1em'}}>
						<Link activeClassName="active" className="btn btn-default" onlyActiveOnIndex to={urlMapping.getUrl(type, 'list', {...flow})}><FormattedMessage defaultMessage="All" id="flowIncomingEmailList.listAll" /></Link>
						<Link activeClassName="active" className="btn btn-default" onlyActiveOnIndex to={urlMapping.getUrl(type, 'list-unprocessed', {...flow})}><FormattedMessage defaultMessage="Unprocessed" id="flowIncomingEmailList.listUnprocessed" /></Link>
						<Link activeClassName="active" className="btn btn-default" onlyActiveOnIndex to={urlMapping.getUrl(type, 'list-failed', {...flow})}><FormattedMessage defaultMessage="Failed" id="flowIncomingEmailList.listFailed" /></Link>
					</div>


                    <div aria-label="..." className="btn-group" role="group" style={{paddingRight: '1em'}}>
                        <LoadingButton className="btn btn-default" disabled={flow.paused || !flow.enabled || flow.flowDef.globallyDisabled || this.props.user.organisation.blocked}  icon="fa-refresh" key='failed-mails' onClick={() => { this.onClickRetryFailedIncomingEmails(); }} ><i className="fa fa-fw fa-envelope"></i> <FormattedMessage defaultMessage="Retry failed e-mails" id="invokeAllButton.retryFailedIncomingEmails" /></LoadingButton>
                        <LoadingButton className="btn btn-default" disabled={flow.paused || !flow.enabled || flow.flowDef.globallyDisabled || this.props.user.organisation.blocked}  icon="fa-refresh" key='unprocessed-mails' onClick={() => { this.onClickRetryUnprocessedIncomingEmails(); }} ><i className="fa fa-fw fa-envelope"></i> <FormattedMessage defaultMessage="Retry unprocessed e-mails" id="invokeAllButton.retryUnprocessedIncomingEmails" /></LoadingButton>
                    </div>

                    <PagingGrid
                      columns={['emailSender', 'emailRecipeint', 'subject', 'createdAt', 'processed', 'failed', 'nextRetry']}
                      edges={list}
                      metadata={type}
                      pageInfo={list.pageInfo}
                      relay={this.props.relay}
                      urlMapping={urlMapping}
                    />

                </div>
            </AppPanel>
        );
    }
}

export default Relay.createContainer(wrapNotFound(injectIntl(FlowIncomingEmailList), (props) => {
    return props.user.organisation && props.user.organisation.flow;
}), {
    initialVariables: {
        organisationId: '',
        flowId: '',
        first: defaultPageSize,
        after: null,
        before: null,
        last: null,
        failed: null,
        processed: null,
        type: null,
    },
    prepareVariables (variables) {

        if (variables.type) {
            if (variables.type === 'failed') {
				variables.failed = true;

			} else if (variables.type === 'unprocessed') {
                variables.processed = false;
            }
		} else {
            variables.failed = null;
            variables.processed = null;
        }


        processPagingParams(variables);

        return variables;
    },
    fragments: {
        user: () => Relay.QL`
            fragment on User {
                organisation(id: $organisationId) {
                    id,
                    realId,
                    name,
                    blocked,
                    flow(id: $flowId) {
                        id,
                        realId,
                        organisation_id,
                        name,
                        paused,
                        enabled,
                        flowDef {
                            id,
                            realId,
                            globallyDisabled,
                        },
                        flowIncomingEmails(first: $first, after: $after, before: $before, last: $last, failed: $failed, processed: $processed) {
                            edges {
                                node {
                                    id,
                                    realId,
                                    flow_id,
                                    nextRetry,
                                    organisation_id,
                                    emailSender,
                                    emailRecepient,
                                    emailSubject,
                                    processed,
                                    failed,
                                    createdAt,
                                }
                            },
                            pageInfo {
                                hasNextPage,
                                hasPreviousPage,
                                startCursor,
                                endCursor
                            }
                        }
                    }
                }
            }
        `,
    },
});
